import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Plus, Trash2, Save, Eye, Copy, Check, 
  Camera, Star, Upload, Image, User, Briefcase, MapPin,
  MessageSquare, Instagram, Mail, X, Loader2, Sparkles, Share2, AlertCircle,
  Palette, ImageIcon
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { portfolioTemplates, generatePortfolioSlug, colorPalettes, getTemplateCoverImages, getDefaultPalette } from '../data/portfolioMock';
import { siteData } from '../data/mock';
import MasterAccess from '../components/MasterAccess';

const MAX_PHOTOS_PER_WORK = 5;

const MeuTrampoConfigurar = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(null); // 'tagline', 'bio', 'description'
  const [aiUsage, setAiUsage] = useState({ remaining: 2 });
  const photoInputRef = useRef(null);
  const workImageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    business: '',
    tagline: '',
    bio: '',
    phone: '',
    email: '',
    instagram: '',
    city: '',
    yearsExperience: '',
    photo: null,
    photoPreview: null,
    template: null,
    colorPalette: null,
    coverPhoto: null,
    customColors: {
      primary: '#8b5cf6',
      secondary: '#7c3aed',
      accent: '#ddd6fe'
    },
    useCustomColors: false,
    categories: [],
    works: [],
    settings: {
      showWhatsApp: true,
      showEmail: true,
      showInstagram: true,
      acceptTestimonials: true
    }
  });

  const [newWork, setNewWork] = useState({
    title: '',
    description: '',
    category: '',
    images: []
  });

  const [newCategory, setNewCategory] = useState('');
  const [photoLimitWarning, setPhotoLimitWarning] = useState(false);

  const slug = formData.business ? generatePortfolioSlug(formData.business) : 'seu-portfolio';
  const fullUrl = `${window.location.origin}/meutrampo/${slug}`;

  // AI Text Generation
  const generateAIText = async (type) => {
    if (aiUsage.remaining <= 0) {
      alert('Você atingiu o limite de 2 gerações de IA por 6 meses.');
      return;
    }

    setAiLoading(type);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const context = {
        name: formData.name,
        business: formData.business,
        categories: formData.categories,
        city: formData.city,
        yearsExperience: formData.yearsExperience,
        title: newWork.title,
        category: newWork.category
      };

      const response = await fetch(`${API_URL}/api/portfolio/professionals/${slug}/generate-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, context })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao gerar texto');
      }

      const data = await response.json();
      
      if (type === 'tagline') {
        setFormData({ ...formData, tagline: data.text });
      } else if (type === 'bio') {
        setFormData({ ...formData, bio: data.text });
      } else if (type === 'work_description') {
        setNewWork({ ...newWork, description: data.text });
      }

      setAiUsage({ remaining: data.remaining_generations });
    } catch (e) {
      alert(e.message);
    } finally {
      setAiLoading(null);
    }
  };

  // Handlers
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photo: reader.result, photoPreview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTemplateSelect = (template) => {
    const templateCovers = getTemplateCoverImages(template.id);
    const defaultPalette = getDefaultPalette(template.id);
    
    setFormData({
      ...formData,
      template: template.id,
      categories: template.id === 'personalizado' ? [] : [...template.categories],
      colorPalette: defaultPalette,
      coverPhoto: templateCovers[0],
      useCustomColors: false
    });
  };

  const handleAddCategory = () => {
    if (newCategory && !formData.categories.includes(newCategory)) {
      setFormData({
        ...formData,
        categories: [...formData.categories, newCategory]
      });
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (cat) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter(c => c !== cat)
    });
  };

  const handleWorkImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentImages = newWork.images.length;
    const availableSlots = MAX_PHOTOS_PER_WORK - currentImages;
    
    if (availableSlots <= 0) {
      setPhotoLimitWarning(true);
      setTimeout(() => setPhotoLimitWarning(false), 3000);
      return;
    }

    const filesToProcess = files.slice(0, availableSlots);
    
    filesToProcess.forEach(file => {
      if (file.size > 2 * 1024 * 1024) {
        alert('Cada imagem deve ter no máximo 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewWork(prev => {
          if (prev.images.length >= MAX_PHOTOS_PER_WORK) return prev;
          return {
            ...prev,
            images: [...prev.images, { url: reader.result, caption: '' }]
          };
        });
      };
      reader.readAsDataURL(file);
    });

    if (files.length > availableSlots) {
      setPhotoLimitWarning(true);
      setTimeout(() => setPhotoLimitWarning(false), 3000);
    }
  };

  const handleRemoveWorkImage = (indexToRemove) => {
    setNewWork(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleAddWork = () => {
    if (newWork.title && newWork.images.length > 0) {
      setFormData({
        ...formData,
        works: [...formData.works, { ...newWork, id: Date.now() }]
      });
      setNewWork({ title: '', description: '', category: '', images: [] });
    }
  };

  const handleRemoveWork = (id) => {
    setFormData({
      ...formData,
      works: formData.works.filter(w => w.id !== id)
    });
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFinish = async () => {
    setLoading(true);
    
    const portfolioData = {
      ...formData,
      slug,
      createdAt: new Date().toISOString()
    };

    // Salvar no backend (futuro)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/portfolio/professionals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(portfolioData)
      });
    } catch (e) {
      console.log('Backend save failed, using localStorage');
    }

    // Salvar localmente
    localStorage.setItem(`portfolio_${slug}`, JSON.stringify(portfolioData));
    
    setLoading(false);
    setStep(5);
  };

  // Step 1: Dados básicos
  const renderStep1 = () => (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Seus dados</h2>
      <p className="text-slate-600 mb-8">Informações básicas do seu perfil</p>
      
      <div className="space-y-6">
        {/* Foto */}
        <div className="flex justify-center">
          <div className="relative">
            {formData.photoPreview ? (
              <div className="relative">
                <img 
                  src={formData.photoPreview} 
                  alt="Foto" 
                  className="w-32 h-32 rounded-full object-cover border-4 border-purple-200"
                />
                <button
                  onClick={() => photoInputRef.current?.click()}
                  className="absolute bottom-0 right-0 p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700"
                >
                  <Camera size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => photoInputRef.current?.click()}
                className="w-32 h-32 rounded-full bg-purple-100 border-2 border-dashed border-purple-300 flex flex-col items-center justify-center hover:bg-purple-50 transition-all"
              >
                <Camera size={24} className="text-purple-400 mb-1" />
                <span className="text-xs text-purple-500">Adicionar foto</span>
              </button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="name">Seu nome</Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="João Silva"
            className="mt-2"
          />
        </div>

        <div>
          <Label htmlFor="business">Nome do negócio/marca</Label>
          <Input
            id="business"
            name="business"
            value={formData.business}
            onChange={handleInputChange}
            placeholder="João Reformas"
            className="mt-2"
          />
          {formData.business && (
            <p className="text-sm text-slate-500 mt-2">
              Seu link: <span className="text-purple-600 font-medium">{slug}</span>
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="tagline">Frase de destaque (opcional)</Label>
            {formData.business && formData.categories.length > 0 && (
              <button
                onClick={() => generateAIText('tagline')}
                disabled={aiLoading === 'tagline' || aiUsage.remaining <= 0}
                className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700 disabled:text-slate-400"
              >
                {aiLoading === 'tagline' ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Sparkles size={12} />
                )}
                Gerar com IA ({aiUsage.remaining} restantes)
              </button>
            )}
          </div>
          <Input
            id="tagline"
            name="tagline"
            value={formData.tagline}
            onChange={handleInputChange}
            placeholder="Transformando sonhos em realidade há 15 anos"
          />
          <p className="text-xs text-slate-400 mt-1">Máximo 10 palavras</p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label htmlFor="bio">Sobre você (opcional)</Label>
            {formData.business && formData.categories.length > 0 && (
              <button
                onClick={() => generateAIText('bio')}
                disabled={aiLoading === 'bio' || aiUsage.remaining <= 0}
                className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700 disabled:text-slate-400"
              >
                {aiLoading === 'bio' ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Sparkles size={12} />
                )}
                Gerar com IA
              </button>
            )}
          </div>
          <textarea
            id="bio"
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            placeholder="Conte um pouco sobre sua experiência e diferenciais..."
            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
            rows={3}
          />
          <p className="text-xs text-slate-400 mt-1">Máximo 50 palavras</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">Cidade</Label>
            <Input
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Volta Redonda, RJ"
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="yearsExperience">Anos de experiência</Label>
            <Input
              id="yearsExperience"
              name="yearsExperience"
              type="number"
              value={formData.yearsExperience}
              onChange={handleInputChange}
              placeholder="10"
              className="mt-2"
            />
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <Button onClick={() => setStep(2)} className="bg-purple-600 hover:bg-purple-700">
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 2: Contato
  const renderStep2 = () => (
    <div className="max-w-xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Contato</h2>
      <p className="text-slate-600 mb-8">Como seus clientes vão te encontrar</p>
      
      <div className="space-y-6">
        <div>
          <Label htmlFor="phone">WhatsApp</Label>
          <Input
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="(24) 99999-9999"
            className="mt-2"
          />
        </div>

        <div>
          <Label htmlFor="email">E-mail (opcional)</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="seu@email.com"
            className="mt-2"
          />
        </div>

        <div>
          <Label htmlFor="instagram">Instagram (opcional)</Label>
          <Input
            id="instagram"
            name="instagram"
            value={formData.instagram}
            onChange={handleInputChange}
            placeholder="@seuinsta"
            className="mt-2"
          />
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={() => setStep(1)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button onClick={() => setStep(3)} className="bg-purple-600 hover:bg-purple-700">
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 3: Template e Categorias
  const renderStep3 = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Área de atuação</h2>
      <p className="text-slate-600 mb-8">Escolha o template que mais combina com você</p>
      
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {portfolioTemplates.map((template) => {
          const Icon = LucideIcons[template.icon] || LucideIcons.Briefcase;
          const isSelected = formData.template === template.id;
          
          return (
            <button
              key={template.id}
              onClick={() => handleTemplateSelect(template)}
              className={`p-4 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-slate-200 hover:border-purple-300'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg bg-${template.color}-100 flex items-center justify-center mb-3`}>
                <Icon size={20} className={`text-${template.color}-600`} />
              </div>
              <h3 className="font-semibold text-slate-900">{template.name}</h3>
              <p className="text-sm text-slate-500">{template.description}</p>
            </button>
          );
        })}
      </div>

      {formData.template && (
        <>
          {/* Paleta de Cores */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette size={20} className="text-purple-600" />
              <Label className="text-lg font-semibold">Escolha as cores</Label>
            </div>
            
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-3 mb-4">
              {colorPalettes.map((palette) => (
                <button
                  key={palette.id}
                  onClick={() => setFormData({ ...formData, colorPalette: palette.id, useCustomColors: false })}
                  className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                    formData.colorPalette === palette.id && !formData.useCustomColors
                      ? 'border-purple-500 ring-2 ring-purple-200'
                      : 'border-transparent hover:border-slate-300'
                  }`}
                  title={palette.name}
                >
                  <div 
                    className={`w-full h-full bg-gradient-to-br ${palette.gradient}`}
                  />
                  {formData.colorPalette === palette.id && !formData.useCustomColors && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <Check size={20} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Opção personalizar */}
            <button
              onClick={() => setFormData({ ...formData, useCustomColors: !formData.useCustomColors })}
              className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                formData.useCustomColors 
                  ? 'border-purple-500 bg-purple-50 text-purple-700' 
                  : 'border-dashed border-slate-300 hover:border-purple-400 text-slate-600'
              }`}
            >
              <Palette size={16} />
              {formData.useCustomColors ? 'Cores personalizadas ativas' : 'Personalizar cores'}
            </button>

            {formData.useCustomColors && (
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Primária</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.customColors.primary}
                      onChange={(e) => setFormData({
                        ...formData,
                        customColors: { ...formData.customColors, primary: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <span className="text-xs text-slate-500">{formData.customColors.primary}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Secundária</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.customColors.secondary}
                      onChange={(e) => setFormData({
                        ...formData,
                        customColors: { ...formData.customColors, secondary: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <span className="text-xs text-slate-500">{formData.customColors.secondary}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Destaque</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.customColors.accent}
                      onChange={(e) => setFormData({
                        ...formData,
                        customColors: { ...formData.customColors, accent: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer border-0"
                    />
                    <span className="text-xs text-slate-500">{formData.customColors.accent}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Imagem de Capa */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon size={20} className="text-purple-600" />
              <Label className="text-lg font-semibold">Imagem de capa</Label>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {getTemplateCoverImages(formData.template).map((img, index) => (
                <button
                  key={index}
                  onClick={() => setFormData({ ...formData, coverPhoto: img })}
                  className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                    formData.coverPhoto === img
                      ? 'border-purple-500 ring-2 ring-purple-200'
                      : 'border-transparent hover:border-slate-300'
                  }`}
                >
                  <img 
                    src={img} 
                    alt={`Capa ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {formData.coverPhoto === img && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Check size={24} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Categorias */}
          <div className="bg-slate-50 rounded-xl p-6">
            <Label className="mb-3 block">Suas especialidades</Label>
            <div className="flex flex-wrap gap-2 mb-4">
              {formData.categories.map((cat, i) => (
                <span 
                  key={i} 
                  className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-1"
                >
                  {cat}
                  <button onClick={() => handleRemoveCategory(cat)} className="hover:text-purple-900">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Adicionar categoria"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
              />
              <Button onClick={handleAddCategory} variant="outline">
                <Plus size={18} />
              </Button>
            </div>
          </div>
        </>
      )}

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={() => setStep(2)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button 
          onClick={() => setStep(4)} 
          className="bg-purple-600 hover:bg-purple-700"
          disabled={!formData.template}
        >
          Próximo
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );

  // Step 4: Trabalhos
  const renderStep4 = () => (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Seus trabalhos</h2>
      <p className="text-slate-600 mb-8">Adicione fotos dos seus melhores projetos</p>
      
      {/* Lista de trabalhos */}
      {formData.works.length > 0 && (
        <div className="space-y-4 mb-8">
          {formData.works.map((work) => (
            <div key={work.id} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex gap-4">
                <img 
                  src={work.images[0]?.url}
                  alt={work.title}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900">{work.title}</h4>
                  <p className="text-sm text-slate-500">{work.category}</p>
                  <p className="text-xs text-slate-400">{work.images.length} foto(s)</p>
                </div>
                <button 
                  onClick={() => handleRemoveWork(work.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Adicionar trabalho */}
      <div className="bg-slate-50 rounded-xl p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Adicionar trabalho</h3>
        
        <div className="space-y-4">
          <div>
            <Label>Título</Label>
            <Input
              value={newWork.title}
              onChange={(e) => setNewWork({ ...newWork, title: e.target.value })}
              placeholder="Ex: Reforma Apartamento 80m²"
              className="mt-2"
            />
          </div>

          <div>
            <Label>Categoria</Label>
            <select
              value={newWork.category}
              onChange={(e) => setNewWork({ ...newWork, category: e.target.value })}
              className="w-full mt-2 p-3 border border-slate-200 rounded-lg"
            >
              <option value="">Selecione...</option>
              {formData.categories.map((cat, i) => (
                <option key={i} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Descrição (opcional)</Label>
              {newWork.title && newWork.category && (
                <button
                  onClick={() => generateAIText('work_description')}
                  disabled={aiLoading === 'work_description' || aiUsage.remaining <= 0}
                  className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-700 disabled:text-slate-400"
                >
                  {aiLoading === 'work_description' ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Sparkles size={12} />
                  )}
                  Gerar com IA
                </button>
              )}
            </div>
            <textarea
              value={newWork.description}
              onChange={(e) => setNewWork({ ...newWork, description: e.target.value })}
              placeholder="Descreva o trabalho..."
              className="w-full p-3 border border-slate-200 rounded-lg resize-none"
              rows={2}
            />
            <p className="text-xs text-slate-400 mt-1">Máximo 30 palavras</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Fotos (máximo {MAX_PHOTOS_PER_WORK})</Label>
              <span className="text-xs text-slate-500">{newWork.images.length}/{MAX_PHOTOS_PER_WORK}</span>
            </div>
            
            {photoLimitWarning && (
              <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-700 text-sm">
                <AlertCircle size={16} />
                Máximo de {MAX_PHOTOS_PER_WORK} fotos por trabalho
              </div>
            )}
            
            <div className="flex flex-wrap gap-2">
              {newWork.images.map((img, i) => (
                <div key={i} className="relative group">
                  <img src={img.url} alt="" className="w-20 h-20 rounded-lg object-cover" />
                  <button
                    onClick={() => handleRemoveWorkImage(i)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={12} />
                  </button>
                  <span className="absolute bottom-1 left-1 bg-black/50 text-white text-xs px-1 rounded">
                    {i + 1}
                  </span>
                </div>
              ))}
              {newWork.images.length < MAX_PHOTOS_PER_WORK && (
                <button
                  onClick={() => workImageInputRef.current?.click()}
                  className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center hover:border-purple-400 transition-all"
                >
                  <Plus size={20} className="text-slate-400" />
                  <span className="text-xs text-slate-400">Foto</span>
                </button>
              )}
            </div>
            <input
              ref={workImageInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleWorkImageUpload}
              className="hidden"
            />
          </div>

          <Button 
            onClick={handleAddWork}
            disabled={!newWork.title || newWork.images.length === 0}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            <Plus size={18} />
            Adicionar trabalho
          </Button>
        </div>
      </div>

      <div className="mt-8 flex justify-between">
        <Button variant="outline" onClick={() => setStep(3)}>
          <ArrowLeft size={18} />
          Voltar
        </Button>
        <Button onClick={handleFinish} className="bg-purple-600 hover:bg-purple-700" disabled={loading}>
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
          Finalizar
        </Button>
      </div>
    </div>
  );

  // Share handlers
  const handleShareWhatsApp = () => {
    const message = `Olha meu portfólio profissional: ${fullUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleShareInstagram = () => {
    navigator.clipboard.writeText(fullUrl);
    alert('Link copiado! Cole na sua bio do Instagram.');
  };

  // Step 5: Sucesso
  const renderStep5 = () => (
    <div className="max-w-xl mx-auto text-center">
      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
        <Check size={40} className="text-green-600" />
      </div>
      
      <h2 className="text-2xl font-bold text-slate-900 mb-2">Portfólio criado!</h2>
      <p className="text-slate-600 mb-8">
        Seu portfólio está pronto para ser compartilhado.
      </p>
      
      <div className="bg-slate-50 rounded-xl p-6 mb-6">
        <p className="text-sm text-slate-500 mb-2">Seu link exclusivo:</p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={fullUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-white rounded-lg border border-slate-200 text-slate-800 text-sm"
          />
          <Button onClick={handleCopyLink} variant="outline">
            {copied ? <Check size={18} /> : <Copy size={18} />}
          </Button>
        </div>
      </div>

      {/* Botões de compartilhamento */}
      <div className="bg-white rounded-xl p-6 mb-6 border border-slate-200">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center justify-center gap-2">
          <Share2 size={18} />
          Compartilhar agora
        </h3>
        <div className="flex gap-3 justify-center">
          <Button 
            onClick={handleShareWhatsApp}
            className="bg-green-500 hover:bg-green-600"
          >
            <MessageSquare size={18} />
            WhatsApp
          </Button>
          <Button 
            onClick={handleShareInstagram}
            variant="outline"
            className="border-pink-300 text-pink-600 hover:bg-pink-50"
          >
            <Instagram size={18} />
            Instagram
          </Button>
          <Button 
            onClick={handleCopyLink}
            variant="outline"
          >
            <Copy size={18} />
            Copiar link
          </Button>
        </div>
      </div>

      <div className="flex gap-4 justify-center mb-8">
        <Button onClick={() => navigate(`/meutrampo/${slug}`)} className="bg-purple-600 hover:bg-purple-700">
          <Eye size={18} />
          Ver meu portfólio
        </Button>
      </div>

      <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
        <h3 className="font-semibold text-purple-900 mb-2">Próximos passos</h3>
        <ul className="text-sm text-purple-800 space-y-2 text-left">
          <li className="flex items-center gap-2">
            <Check size={16} className="text-purple-500" />
            Compartilhe o link no WhatsApp
          </li>
          <li className="flex items-center gap-2">
            <Check size={16} className="text-purple-500" />
            Coloque na bio do Instagram
          </li>
          <li className="flex items-center gap-2">
            <Check size={16} className="text-purple-500" />
            Peça avaliações aos seus clientes
          </li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={siteData.brand.logo} alt="AtivaJá" className="h-8" />
            <span className="text-slate-400">|</span>
            <span className="font-medium text-slate-700">Meu Trampo</span>
          </a>
          
          {step < 5 && (
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s === step
                      ? 'bg-purple-600 text-white'
                      : s < step
                      ? 'bg-purple-100 text-purple-600'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {s < step ? <Check size={16} /> : s}
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="py-12 px-4">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </main>

      {/* Master Access */}
      <MasterAccess 
        produto="meutrampo" 
        slug={slug}
        onSuccess={() => navigate(`/meutrampo/painel/${slug}`)}
      />
    </div>
  );
};

export default MeuTrampoConfigurar;
